import Chip from 'components/Chip'
import ContentBox from 'components/ContentBox'
import Row from 'components/Row.js'
import TextBody from 'components/TextBody'
import TextTitleCondensed from 'components/TextTitleCondensed'
import { TryPremiumFreeButton } from 'components/TryPremiumFreeButton'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import {
  isMobileScreenViewportSelect,
  isSmallMobileScreenViewportSelect
} from 'modules/viewport/selectors'
import PropTypes from 'prop-types'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import {
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_PRICE,
  SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE
} from '@gymaholic/packages'
import SubscriptionPremiumPlanFeatures from 'pages/Premium/FreeVsPremiumFeatures/SubscriptionPlans/Premium/Features/index.js'
import { getSelectedSubscriptionPlanSelect } from 'modules/training/modules/training-premium/selectors/get-selected-subscription-plan/index.js'
import { isYearlySubscriptionUtil } from 'modules/training/modules/subscription/utils/is-yearly-subscription/index.js'
import { hasPremiumUserSelect } from 'modules/training/modules/training-users/selectors/has-premium/index.js'
import { TopRightOverlay } from 'components/TopRightOverlay/index.js'
import { hasPremiumPlusUserSelect } from 'modules/training/modules/training-users/selectors/has-premium-plus/index.js'
import TextBodySmaller from 'components/TextBodySmaller.js'
import { hasFreeTrialEndedUserSelect } from 'modules/training/modules/training-users/selectors/has-free-trial-ended/index.js'
import { SUBSCRIPTION_PLAN_YEARLY_SAVINGS } from 'modules/subscription/constants/index.js'
import TextBodySmall from 'components/TextBodySmall/index.js'
import { shouldShowNewCustomerDiscountUserSelect } from 'modules/training/modules/training-users/selectors/shouldShowNewCustomerDiscount/index.js'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen/index.js'

const messages = defineMessages({
  title: {
    id: 'SubscriptionPremiumPlan.title',
    defaultMessage: 'Premium ⭐️'
  },
  freeTrialCancelAnytime: {
    id: 'SubscriptionPremiumPlan.freeTrialCancelAnytime',
    defaultMessage: '$0 due today. 7-day free trial. Cancel any time.'
  },
  freeTrialCancelAnytimeTrialEnded: {
    id: 'SubscriptionPremiumPlan.freeTrialCancelAnytimeTrialEnded',
    defaultMessage: 'Start your subscription today. Cancel any time.'
  },
  cancelAnytime: {
    id: 'SubscriptionPremiumPlan.cancelAnytime',
    defaultMessage: 'Cancel any time.'
  },
  monthlyPrice: {
    id: 'SubscriptionPremiumPlan.monthlyPrice',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${monthlyPrice} / month'
  },
  priceWithoutDiscount: {
    id: 'SubscriptionPremiumPlan.priceWithoutDiscount',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${priceWithoutDiscount}'
  },
  priceYearlyPerMonth: {
    id: 'SubscriptionPremiumPlan.priceYearlyPerMonth',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${monthlyPrice} / month'
  },
  saveAmountPerYear: {
    id: 'SubscriptionPremiumPlan.saveAmountPerYear',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: 'Save ${amountSavedPerYear}/year'
  },
  yourPlan: {
    id: 'SubscriptionPremiumPlan.yourPlan',
    defaultMessage: 'Your plan'
  },
  priceYearly: {
    id: 'SubscriptionPremiumPlan.priceYearly',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${yearlyPrice} / year'
  },
  bestFor: {
    id: 'SubscriptionPremiumPlan.bestFor',
    defaultMessage: 'For those focused on serious training.'
  }
})

/**
 * @param props
 * @param props.isSummary
 * @param props.isReadOnly
 * @param props.isCompact
 * @param props.areFeaturesPressable,
 * @param props.shouldBoldMainFeatures
 * @return {Element}
 * @constructor
 */
export function SubscriptionPremiumPlan (props) {
  const {
    isSummary,
    isCompact,
    areFeaturesPressable,
    shouldBoldMainFeatures
  } = props

  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  const selectedPlan = useSelector(getSelectedSubscriptionPlanSelect)
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)
  const isSmallMobileScreen = useSelector(isSmallMobileScreenViewportSelect)
  const hasPremium = useSelector(hasPremiumUserSelect)
  const hasPremiumPlus = useSelector(hasPremiumPlusUserSelect)
  const intl = useIntl()
  const hasFreeTrialEnded = useSelector(hasFreeTrialEndedUserSelect)
  const shouldShowNewCustomerDiscount = useSelector(shouldShowNewCustomerDiscountUserSelect)

  // TODO move to selector
  const isYearly = isYearlySubscriptionUtil(selectedPlan)
  const isLandingPage = !selectedPlan
  let priceWithoutDiscount
  let yearlyPrice
  let monthlyPrice
  let priceLabelElement
  let priceWithoutDiscountElement
  let priceSuffixElement
  let amountSavedPerYear
  const hasOnlyPremium = hasPremium && !hasPremiumPlus
  if (isLandingPage || isYearly) {
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_YEARLY_2025
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_2025]
    yearlyPrice = SUBSCRIPTION_PLAN_PRICE[subscriptionPlan]
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[subscriptionPlan]
    amountSavedPerYear = SUBSCRIPTION_PLAN_YEARLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmall>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmall>
        </s>
      </View>
    )
    priceSuffixElement = (
      <Chip ml>
        {formatMessageIntlUtil(intl, messages.priceYearly, { yearlyPrice })}
      </Chip>
    )
  } else {
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[SUBSCRIPTION_PLAN.PREMIUM_2025]
    priceLabelElement = formatMessageIntlUtil(intl, messages.monthlyPrice, { monthlyPrice })
  }

  const style = {
    position: 'relative'
  }
  if (isMobileScreen) {
    style.width = 'calc(100vw - 5rem)'
  } else {
    style.width = '18em'
  }

  let freeTrialMessage
  if (hasPremium) {
    freeTrialMessage = messages.cancelAnytime
  } else if (hasFreeTrialEnded) {
    freeTrialMessage = messages.freeTrialCancelAnytimeTrialEnded
  } else {
    freeTrialMessage = messages.freeTrialCancelAnytime
  }

  return (
    <ContentBox
      pt={isCompact ? 2 : 3}
      pb={2}
      mb={0}
      style={style}
      bd={isCompact}
    >
      <View
        style={{
          alignItems: isSummary
            ? View.ALIGN.START
            : View.ALIGN.CENTER,
          justifyContent: View.JUSTIFY.CENTER,
          width: '100%',
          height: '100%'
        }}
      >
        {
          !!hasOnlyPremium && (
            <TopRightOverlay>
              <span>{formatMessageIntlUtil(intl, messages.yourPlan)}</span>
            </TopRightOverlay>
          )
        }

        <h3 style={{ height: '1.7em' }}>
          <TextTitleCondensed style={{
            display: 'inline-block',
            height: '100%'
          }}
          >
            {formatMessageIntlUtil(intl, messages.title)}
          </TextTitleCondensed>
        </h3>

        <TextBodySmaller
          c='paragraph'
          mb
          style={{
            textAlign: isSummary ? 'left' : 'center'
          }}
        >
          {formatMessageIntlUtil(intl, messages.bestFor)}
        </TextBodySmaller>

        {
          // !isCompact && (
          <SubscriptionPremiumPlanFeatures
            isSummary={isSummary}
            areFeaturesPressable={areFeaturesPressable}
            shouldBoldMainFeatures={shouldBoldMainFeatures}
          />
          // )
        }

        {
          !!shouldShowNewCustomerDiscount &&
          !isSummary &&
          !isSmallScreen && (
            <View style={{
              height: '16px'
            }}
            />
          )
        }

        <Row
          ac
          mt={2}
          style={{
            fontSize: isSmallMobileScreen ? '0.8rem' : null
          }}
        >
          {priceWithoutDiscountElement}
          <TextBody
            b
          >
            {priceLabelElement}
          </TextBody>
          {priceSuffixElement}
        </Row>

        {
          !!amountSavedPerYear && (
            <View>
              <TextBodySmaller
                fw={500}
                ta={isSummary ? 'left' : 'center'}
              >
                {
                  formatMessageIntlUtil(intl, messages.saveAmountPerYear, {
                    amountSavedPerYear
                  })
                }
              </TextBodySmaller>
            </View>
          )
        }

        <View>
          <TextBodySmaller
            c='paragraph'
            ta={isSummary ? 'left' : 'center'}
          >
            {formatMessageIntlUtil(intl, freeTrialMessage)}
          </TextBodySmaller>
        </View>

        {
          !amountSavedPerYear &&
          !isSummary &&
          !isSmallScreen && (
            <View style={{
              height: '16.5px'
            }}
            />
          )
        }

        {
          !isSummary && (
            <TryPremiumFreeButton
              mt={2.5}
              w
            />
          )
        }
      </View>
    </ContentBox>
  )
}

SubscriptionPremiumPlan.propTypes = {
  isReadOnly: PropTypes.bool,
  isSummary: PropTypes.bool
}
